import { AxiosInstance, AxiosResponse } from 'axios'
import _ from 'lodash'
import { all, call, getContext, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'
import { PaginationConfig } from 'antd/lib/pagination'

import { apiCall } from '../../../utils/api'
import { downloadFile } from '../../../utils/download'
import { waitForConfigLoad } from '../../../utils/sagaUtils'
import { Dictionary } from '../../../utils/types'
import { getCountryCode, getInvoicesPageSize } from '../../config'
import { withLoading, withLoadingParametrized } from '../../loading'
import { ColumnSorting, TableActions, sortParam } from '../../table'
import { selectPagination, selectSorting } from '../../table/selectors'

import { InvoiceActions } from './actions'
import { getInvoicesFilter, getInvoicesFilters } from './selectors'
import { Invoice, InvoiceInfo } from './types'

type InvoicesResponse = {
  invoices: {
    id: string
    sapInvoiceId: string
    sentDate: string
    amount: string
    orderIds: string[]
  }[]
  meta: {
    totalCount: number
    pageCount: number
    pageNumber: number
  }
}

const mapResponseToInvoices = (res: InvoicesResponse): Invoice[] => {
  return _.map(res.invoices, x => ({ ...x }))
}

const preprocessFilters = (filters: Dictionary<any>) =>
  _.mapValues(filters, filter => (_.has(filter, 'value') ? _.get(filter, 'value') : filter))

export function* fetchInvoices(action: ReturnType<typeof InvoiceActions.fetchData>) {
  yield call(waitForConfigLoad)
  const { payload } = action

  const countryCode: string = yield select(getCountryCode)
  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'invoices' })
  const filter: string = yield select(getInvoicesFilter)
  const filters: Dictionary<any> = yield select(getInvoicesFilters)

  const preprocessedFilters = preprocessFilters(filters)

  const { data }: AxiosResponse<InvoicesResponse> = yield call(apiCall, {
    url: '/api/invoice/history',
    method: 'post',
    params: {
      domainCode: countryCode,
      page: payload.page,
      pageSize: payload.pageSize,
      sortRequest: sortParam(sorting),
      searchByOrder: filter || undefined
    },
    data: {
      ...preprocessedFilters,
      orderIds: preprocessedFilters?.orderIds
        ?.split(',')
        .map((v: string) => v.trim())
        .filter(Boolean)
    }
  })

  yield put(
    InvoiceActions.setData({
      data: mapResponseToInvoices(data),
      page: data.meta.pageNumber,
      totalPages: data.meta.pageCount
    })
  )

  if (data.meta.pageNumber === 1) {
    yield put(
      TableActions.setPagination({
        tableKey: 'invoices',
        pagination: {
          current: data.meta.pageNumber,
          pageSize: payload.pageSize,
          total: data.meta.totalCount
        }
      })
    )
  }
}

export function* reloadOnSort(action: ReturnType<typeof TableActions.setSorting>) {
  if (action.payload.tableKey !== 'invoices') return

  const filter: string = yield select(getInvoicesFilter)
  const pageSize: number = yield select(getInvoicesPageSize)

  yield put(
    InvoiceActions.fetchData({
      filter,
      page: 1,
      pageSize
    })
  )
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'invoices') return

  const filter: string = yield select(getInvoicesFilter)
  const { pagination } = payload

  yield put(
    InvoiceActions.fetchData({
      filter,
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

export function* loadOnFilterChange() {
  const pagination: PaginationConfig = yield select(selectPagination, { tableKey: 'invoices' })
  const filter: string = yield select(getInvoicesFilter)

  yield put(
    InvoiceActions.fetchData({
      filter,
      page: 1,
      pageSize: pagination.pageSize || pagination.defaultPageSize || 10
    })
  )
}

export function* loadInvoice(action: ReturnType<typeof InvoiceActions.loadInvoiceDetails>) {
  const { invoiceId } = action.payload
  const countryCode: string = yield select(getCountryCode)

  const { data }: AxiosResponse<InvoiceInfo> = yield call(apiCall, {
    url: '/api/invoice/info',
    method: 'get',
    params: {
      id: invoiceId,
      domainCode: countryCode
    }
  })

  yield put(
    InvoiceActions.setInvoiceDetails({
      invoiceId: invoiceId,
      invoiceData: data
    })
  )
}

export function* downloadInvoice({ payload }: ReturnType<typeof InvoiceActions.downloadInvoice>) {
  const { invoiceId, downloadInvoiceName } = payload
  const axios: AxiosInstance = yield getContext('axios')
  const {
    data
  }: AxiosResponse<{
    token: string
    documentId: string
    expirationDate: string
  }> = yield call(apiCall, {
    url: '/api/Download/invoice/token',
    method: 'get',
    params: {
      id: invoiceId
    }
  })

  yield call(
    downloadFile,
    `${axios.defaults.baseURL || ''}/api/Download/invoice/report?id=${invoiceId}&token=${data.token}`,
    document.body,
    downloadInvoiceName
  )
}

/* istanbul ignore next */
export function* invoicesSagas() {
  yield all([
    takeLatest(getType(InvoiceActions.fetchData), withLoading('invoices', fetchInvoices)),
    takeLatest(getType(TableActions.setSorting), reloadOnSort),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange),
    takeLatest(getType(InvoiceActions.setFilters), loadOnFilterChange),
    takeLatest(getType(InvoiceActions.setFilter), loadOnFilterChange),
    takeLatest(getType(InvoiceActions.clearFilter), loadOnFilterChange),
    takeLatest(getType(InvoiceActions.downloadInvoice), downloadInvoice),
    takeEvery(
      getType(InvoiceActions.loadInvoiceDetails),
      withLoadingParametrized('invoices', loadInvoice, x => x.payload.invoiceId)
    )
  ])
}
